import React, { KeyboardEvent, ClipboardEvent, useState } from 'react';
import { createAccount } from '../../dal/data-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../domain/captcha/simple-captcha';
import { getIsChina, getIsAuthenticated } from '../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../domain/captcha/recaptcha3-terms';
import LogoutScreen from '../../logout/logout-screen';
import { getUTMFromStorage } from '../../../../domain/marketing/utm/utm-provider';
import { getPageNodeGUID } from '../../../../domain/common';
import { IFormControl } from '../../../../ui/forms/interfaces';
import FormBorder from '../../../../ui/forms/form-border';
import FormTitle from '../../../../ui/forms/form-title';
import FormTextField from '../../../../ui/forms/fields/text-field';
import ChinaCaptchaField from '../../../../ui/forms/fields/captcha-china-field';
import FormPreloader from '../../../../ui/forms/form-preloader';
import { CreateAccountReferrer } from '../../enums/create-account-referrer';
import ExternalLogin from '../../login/external-login/external-login';
import { sendRegistrationEvents } from '../../../../domain/marketing/marketing-provider';
import { isInAppBrowser, MOBILE_WEBVIEW_WARNING_TEXT } from '../../../../domain/inapp-detection';

/**
 * Create Account app
 * @constructor
 */
const App = () => {

    const isChina = getIsChina();
    const isUserAuth = getIsAuthenticated();

    // validation ---------------------------------------
    const emailControl = createControl('email', '', {
        required: true,
        email: true,
    });
    const passwordControl = createControl('password', '', {
        required: true,
        password: true,
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [email, setEmail] = useState<IFormControl>(emailControl);
    const [password, setPassword] = useState<IFormControl>(passwordControl);

    // simple captcha - used for china
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /**
     * once user created account, redirect to the confirmation page
     */
    const performRedirect = () => {

        // remove current page from history,
        // so when user clicks 'back button' it will skip the login page
        window.location.replace('/activate-account');
    };

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * perform user login on server
     */
    const performCreateAccount = async () => {

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [email, setEmail],
                    [password, setPassword],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [email, setEmail],
                    [password, setPassword],
                ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            const utm = getUTMFromStorage();

            response = await createAccount(
                CreateAccountReferrer.GeneralCreateAccount,
                email.value,
                password.value,
                captchaToken,
                recaptchaVersion,

                // message gears -------
                getPageNodeGUID(),
                utm ? utm.firstSource : '',
                utm ? utm.firstMedium : '',
                utm ? utm.firstCampaign : '',
                utm ? utm.lastSource : '',
                utm ? utm.lastMedium : '',
                utm ? utm.lastCampaign : '',
            );
        }
        catch(ex){}

        // all is well ---------------
        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            await sendRegistrationEvents(); // email.value

            // redirect to the confirm account page
            performRedirect();
            return;
        }

        // handle errors ---------
        setIsLoading(false);

        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    };

    /**
     * enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performCreateAccount();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    // https://wavesaudio.atlassian.net/browse/DSWEB-4506
    const isMobileWebview = isInAppBrowser()?.isInAppBrowser ?? false;

    return (
        <>
            {
                !isUserAuth &&
                <FormBorder hideTerms={true}>
                    <FormTitle
                        title={'Create your Waves account'}
                        marginBottom={ 'mb-4' }
                    />

                    {
                        !isMobileWebview &&
                        <ExternalLogin
                            facebookText="Sign up with Facebook"
                            googleTextParam="signup_with"
                            dividerText="Or, Create Account"
                        />
                    }

                    <FormTextField
                        id={'email-tb'}
                        qa="sign-up-email"
                        qaError="sign-up-error-msg"
                        label={'Email'}
                        control={email}
                        maxLength={80}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setEmail(updateControl(email, $target.value, !email.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setEmail(validateControl(email));
                        }}
                        onClick={handleFieldClick}
                        onKeyUp={handleEnter}
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                        }}
                        comment={
                            <div className="text-sm text-gray-400">An activation link will be sent to this
                                email.</div>

                        }
                    />

                    <FormTextField
                        id={'password-tb'}
                        qa="sign-up-psw"
                        qaError="sign-up-error-msg"
                        label={'Password'}
                        control={password}
                        maxLength={110}
                        type={'password'}
                        onChange={evt => {
                            const $target = evt.target as HTMLInputElement;
                            setPassword(updateControl(password, $target.value, !password.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={evt => {
                            setPassword(validateControl(password));
                        }}
                        onClick={handleFieldClick}
                        onKeyUp={handleEnter}
                        onPaste={(evt: ClipboardEvent<HTMLInputElement>) => {
                            setPassword(updateControl(password, removeInvisibleCharsOnPaste(evt)));
                        }}
                        comment={
                            <div className="text-sm text-gray-400">At least 8 characters.</div>
                        }
                    />

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha"
                             className={`mb-4 flex justify-center recaptcha recaptcha-version-${recaptchaVersion}`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={simpleCaptchaCode}
                            setSimpleCaptchaCode={setSimpleCaptchaCode}
                            simpleCaptchaAttempts={simpleCaptchaAttempts}
                        />
                    }

                    <div className="mb-4 text-sm text-center">
                        By clicking submit I agree to the <a href="/legal/eula">End User License Agreement</a>, <a href="/legal/privacy-policy">Privacy Notice</a>, and <a href="/legal/waves-terms-of-use">Waves Terms of Use</a>.
                    </div>

                    <div className="mb-2">

                        {isLoading && <FormPreloader/>}

                        {
                            !isLoading &&
                            <button
                                className="w-btn"
                                type="button"
                                data-qa="sign-up-submit-btn"
                                onClick={async (evt) => {
                                    await performCreateAccount();
                                }}
                            >Submit</button>
                        }
                    </div>

                    {
                        response && !response.isValid && (
                            <div className="text-sm text-red my-4 text-center" data-qa="sign-up-error-box" role="alert">
                                <ValidationErrors errors={response.errors} qa="sign-up-error-msg"/>
                            </div>
                        )
                    }

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={recaptcha2WidgetID}
                             role="alert">{captchaError}</div>
                    }

                    <div className="text-sm text-center">
                        Already have an account? <a className="no-underline" href="/login" title="">Log in</a>
                    </div>

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text/>
                        </div>
                    }

                    {
                        isMobileWebview &&
                        <div className="msg-box mt-4">
                            <p>{ MOBILE_WEBVIEW_WARNING_TEXT }</p>
                        </div>
                    }

                </FormBorder>
            }

            {isUserAuth && <LogoutScreen/>}
        </>

    )
};

export default App;